import { defineStore } from 'pinia';
import { createStorage, storage } from '@/utils/Storage';
import { store } from '@/store';
import { ACCESS_TOKEN, LOGIN_STATE, PASSWORD_STATUS } from '@/store/mutation-types';
import { ResultCodeEnum } from '@/enums/httpEnum';
import { checkCookieApi, getMemberInfoApi, loginApi } from '@/api/user';
import { LoginRequest, UserInfo, UserSetting } from '@/api/user/type';
import { IUserState, Member } from './types/user';
import { useAccountSetting } from '@/hooks/setting/useAccountSetting';

const Storage = createStorage({ storage: localStorage });
// import { getUserInfo, login } from '@/api/system/user';

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): IUserState => ({
    token: Storage.get(ACCESS_TOKEN, ''),
    loginState: Storage.get(LOGIN_STATE, ''),
    username: '',
    welcome: '',
    avatar: '',
    permissions: [],
    /**
     * 收藏sku数量
     */
    favoriteSkuCount: 0,
    /**
     * 收藏商城数量
     */
    favoritesShopCount: 0,
    /**
     * 关联商城数量
     */
    monitoredShopsCount: 0,
    change: 0,
    /**
     * 主商城名称
     */
    mallName: '',
    mallId: '',
    member: {
      id: 0,
      account: '',
      mobile: '',
      email: '',
      source: '',
      brandSR: 0,
      chargingStartTime: 0,
      chargingEndTime: 0,
      status: false,
      roleId: 0,
      loginNum: 0,
      lastLoginTime: 0,
      brandSwitch: 0,
      photo: '',
      token: null,
    },
    simpleIndex: {
      id: '',
      mallId: '',
      totalGoodsCount: 0,
      commonGoodsCount: 0,
      totalCommonGoodsPrice: 0,
      totalRmbCommonGoodsPrice: 0,
      originaCoefficient: 0,
      optimalCoefficient: 0,
      score: 0,
      originaScore: 0,
      brandStoreScore: null,
      createTime: 0,
      version: '',
      cmsRule: '',
      originaRanking: 0,
      originaChange: 0,
      mallRanking: 0,
      rankingChange: -1,
    },
  }),
  getters: {
    getToken(): string {
      return this.token;
    },
    getAvatar(): string {
      return this.member?.photo || '';
    },
    getNickname(): string {
      return this.username;
    },
    getPermissions(): string[] {
      return this.permissions;
    },
    getUserInfo(): Member | any {
      return this.member || {};
    },
    getLoginState(): string | null {
      return storage.get(LOGIN_STATE);
    },
    /**
     * 获取用户过期剩余时间 剩余天数
     * @returns
     */
    getAccountValidity(): number {
      const current = new Date().getTime();
      const end = this.member.chargingEndTime;
      return Math.ceil((end - current) / 1000 / 60 / 60 / 24);
    },
    /**
     * 获取用户主商城id
     * @returns
     */
    getAccountMainShopId(): string {
      // if (!this.simpleIndex) {
      //   window['$message'].error('无法获取用户主商城ID');
      // }
      return this.mallId;
    },
    /**
     * 获取用户主商城名称
     * @returns
     */
    getAccountMainShopName(): string {
      return this.mallName;
    },
  },
  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setPermissions(permissions) {
      this.permissions = permissions;
    },
    setLoginState() {
      this.loginState = '1';
      Storage.set(LOGIN_STATE, '1');
    },
    removeLoginState() {
      this.loginState = '';
      Storage.removeCookie('account');
      Storage.remove(LOGIN_STATE);
    },
    setUserInfo(state: UserInfo) {
      this.member = state.member || {};
      this.simpleIndex = state.simpleIndex || {};
      this.mallName = state.mallName || '';
      this.mallId = state.mallId || '';
      this.change = state.change || 0;
      this.favoriteSkuCount = state.favoriteSkuCount || 0;
      this.favoritesShopCount = state.favoritesShopCount || 0;
      this.monitoredShopsCount = state.monitoredShopsCount || 0;
    },
    updateUserSetting(setting: UserSetting) {
      this.mallName = setting.mallName;
      this.member.source = setting.source;
      this.member.photo = setting.photo;
      this.member.email = setting.email;
      console.log(1222, this.member);
    },
    // 登录
    async login(userInfo: LoginRequest) {
      try {
        const response = await loginApi(userInfo);
        const { code } = response;
        if (code === ResultCodeEnum.SUCCESS) {
          this.setLoginState();
          // await this.GetInfo();
          // this.setToken(result.token);
          // this.setUserInfo(result);
        }
        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    // 获取用户信息
    GetInfo(): Promise<UserInfo> {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const that = this;
      const accountSetting = useAccountSetting();
      return new Promise(async (resolve, reject) => {
        try {
          const result = await getMemberInfoApi();
          if (result) {
            that.setUserInfo(result);
            that.setPermissions([result.member.roleId.toString()]);
            accountSetting.init();
            storage.set(PASSWORD_STATUS, result.member.loginNum);
            resolve({ ...result, permissions: [result.member.roleId.toString()] });
          } else {
            that.removeLoginState();
            reject(new Error('获取用户信息失败'));
          }
        } catch (e) {
          window['$message'].error('Get user info error');
          that.removeLoginState();
          reject(e);
        }
      });
    },

    // 登出
    async logout() {
      this.setPermissions([]);
      storage.removeCookie('account');
      this.removeLoginState();
      this.member = {
        id: 0,
        account: '',
        mobile: '',
        email: '',
        source: '',
        brandSR: 0,
        chargingStartTime: 0,
        chargingEndTime: 0,
        status: false,
        roleId: 0,
        loginNum: 0,
        lastLoginTime: 0,
        brandSwitch: 0,
        photo: '',
        token: null,
      };
      this.simpleIndex = {
        id: '',
        mallId: '',
        totalGoodsCount: 0,
        commonGoodsCount: 0,
        totalCommonGoodsPrice: 0,
        totalRmbCommonGoodsPrice: 0,
        originaCoefficient: 0,
        optimalCoefficient: 0,
        score: 0,
        originaScore: 0,
        brandStoreScore: null,
        createTime: 0,
        version: '',
        cmsRule: '',
        originaRanking: 0,
        originaChange: 0,
        mallRanking: 0,
        rankingChange: -1,
      };
      this.mallName = '';
      this.mallId = '';
      this.change = 0;
      this.favoriteSkuCount = 0;
      this.favoritesShopCount = 0;
      this.monitoredShopsCount = 0;
      return Promise.resolve('');
    },
    /**
     * 验证cookie是否过期
     */
    async checkCookie() {
      return await checkCookieApi();
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWidthOut() {
  return useUserStore(store);
}
